import React from 'react';
import momentTZ from 'moment-timezone';

import { CalendarGraphic } from '@eventbrite/eds-iconography';
import { EmptyState } from '@eventbrite/eds-empty-state';
import {
    getEventsById,
    EventCard,
    useEvents,
    transformDestinationEvent,
    CONSUMER_CARD_DEFAULTS,
} from '@eventbrite/event-renderer';
import { gettext } from '@eventbrite/i18n';

import EventCardGroupEdit from '../../components/EventCardGroupEdit';
import { EVENTCARD_WRAPPER_CLASSES } from './constants';

const updateEditorialEvents = (editorialEvents, expandedEvents) => {
    const normalizedEditorialEvents = editorialEvents.reduce((memo, event) => {
        return {
            ...memo,
            [event.id]: event,
        };
    }, {});

    const mergedEvents = expandedEvents.map((expandedEvent) => {
        const editorialEvent =
            normalizedEditorialEvents[expandedEvent.id] || {};

        //Ensure that the custom name and image overrides persist
        //even when we expand other fields of the event.
        const editorialName = editorialEvent?.name || expandedEvent.name;
        const editorialImage =
            editorialEvent?.imageUrl || expandedEvent.imageUrl;

        return {
            ...editorialEvent,
            ...expandedEvent,
            name: editorialName,
            imageUrl: editorialImage,
        };
    });

    return mergedEvents;
};

const formatEditorialEvents = (events = {}) => {
    const collectionEvents = events.collection_events;

    const formattedEvents = collectionEvents.map((event) => {
        const formattedEvent = transformDestinationEvent(event.event);
        formattedEvent.name = event.custom_title
            ? event.custom_title
            : formattedEvent.name;
        formattedEvent.imageUrl = event.custom_image
            ? event.custom_image.url
            : formattedEvent.imageUrl;
        formattedEvent.customDescription = event.custom_description;

        return formattedEvent;
    });

    return formattedEvents;
};

const sortEventsByDate = (events = []) => {
    return events.sort((eventA, eventB) => {
        const { startDate: a, startTime: b, timezone: timezoneA } = eventA;
        const { startDate: c, startTime: d, timezone: timezoneB } = eventB;

        const dateA = momentTZ.tz(`${a} ${b}`, `${timezoneA}`).valueOf();
        const dateB = momentTZ.tz(`${c} ${d}`, `${timezoneB}`).valueOf();

        //Events often start at the same time and so the
        //sort can output inconsistent values when time
        //equal across two events. In that case simply
        //sort by the event name to get consistent values
        if (dateA - dateB === 0) {
            //Use localeCompare to ensure unicode characters handled
            return eventA.name.localeCompare(eventB.name);
        } else {
            return dateA - dateB;
        }
    });
};

/**
 * Editorial collections do not currently support pagination. We have a limit
 * on the number of events that a given collection can contain
 * and sort the response on the client.
 */
export const EditorialEventRenderer = (props) => {
    const { data } = useEvents(
        ['editorial-collection-events', props.userId, props.collectionId],
        () =>
            Promise.resolve({
                events: sortEventsByDate(formatEditorialEvents(props.events)),
            }),
        {
            initialData: () => {
                const events = sortEventsByDate(
                    formatEditorialEvents(props.events),
                );
                return { events, pagination: props.events.pagination };
            },
            expandEventsOnMount: true,
            customExpandFunc: async ({ events }) => {
                const ids = events.map(({ id }) => id);
                const expandedEvents = await getEventsById(ids, 20);

                return {
                    events: updateEditorialEvents(
                        events,
                        expandedEvents?.events,
                    ),
                };
            },
        },
    );

    if (!data.events || data.events.length === 0) {
        return (
            <div className="eds-l-mar-top-12" data-spec="no-events">
                <EmptyState
                    graphicType={<CalendarGraphic />}
                    description={gettext('Your calendar is wide open!')}
                    title={gettext("Let's find your next favorite thing.")}
                />
            </div>
        );
    }

    if (props.isEditMode) {
        return (
            <EventCardGroupEdit
                name="eventsToRemove"
                events={data.events}
                isEditMode={props.isEditMode}
            />
        );
    }

    return (
        <section className="eds-group editorial-event-renderer">
            {data.events.map((eventData) => (
                <div className={EVENTCARD_WRAPPER_CLASSES} key={eventData.id}>
                    <EventCard
                        {...CONSUMER_CARD_DEFAULTS}
                        currencyFormat={props.currencyFormat}
                        affCode={props.affCode}
                        eventData={eventData}
                        gaCategory={props.gaCategory}
                        isAuthenticated={props.isAuthenticated}
                        user={{ id: props.userId }}
                    />
                    <p className="eds-text-bl eds-text-weight--regular eds-l-pad-hor-4">
                        {eventData.customDescription}
                    </p>
                </div>
            ))}
        </section>
    );
};
